import { useCallback, useState } from 'react'
import type { ZodType } from 'zod'
import { ApiError } from './api'

/**
 * يوحّد أخطاء النموذج من مصدرين: تحقّق Zod محلياً، وأخطاء الحقول من الخادم.
 * كلاهما يستعمل نفس المخططات، فالرسائل العربية واحدة في الطرفين.
 */
export function useFormErrors() {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const clear = useCallback(() => {
    setErrors({})
    setFormError(null)
  }, [])

  /** يتحقّق محلياً ويُرجع البيانات المُحلَّلة أو null مع ضبط الأخطاء */
  const validate = useCallback(<T,>(schema: ZodType<T>, value: unknown): T | null => {
    const parsed = schema.safeParse(value)
    if (parsed.success) {
      clear()
      return parsed.data
    }
    const next: Record<string, string> = {}
    for (const issue of parsed.error.issues) {
      const key = issue.path.join('.') || '_'
      next[key] ??= issue.message
    }
    setErrors(next)
    setFormError(null)
    return null
  }, [clear])

  /** يحوّل خطأ الخادم إلى أخطاء حقول أو رسالة عامة */
  const handleApiError = useCallback((err: unknown, fallback = 'حدث خطأ غير متوقع') => {
    if (err instanceof ApiError) {
      if (err.fields && Object.keys(err.fields).length) {
        setErrors(err.fields)
        setFormError(null)
      } else {
        setErrors({})
        setFormError(err.message)
      }
      return
    }
    setErrors({})
    setFormError(fallback)
  }, [])

  return { errors, formError, setFormError, clear, validate, handleApiError }
}
